/* istanbul ignore file */

import React, { memo, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import { LI, UL } from '@expo/html-elements';

import { useA11ySelector } from '@sg/a11y-helpers';
import {
  capitalizeFirstLetter,
  getSubstringsByPositions,
  HighlightWords,
  Icon,
  theme,
  usePressableState,
  useResponsive,
  webOnlyStyles,
} from '@sg/garnish';

import { type AddressDetails } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchAddressesList = memo((props: LocationSearchAddressesListProps) => {
  const { addresses, onPress } = props;

  const { match } = useResponsive();
  const a11ySelector = useA11ySelector('locations.delivery.addresses-container'); // prettier-ignore

  return (
    <View style={match([styles.containerXS, styles.containerSM])} {...a11ySelector.props}>
      <UL>
        {addresses.map((customerAddress) => {
          const address = {
            id: customerAddress.googlePlaceId ?? customerAddress.addressId,
            address: customerAddress.formattedAddress,
            name: customerAddress.name,
          };

          return (
            <LI key={customerAddress.googlePlaceId}>
              <LocationSearchAddress
                variation="customer-address"
                address={address}
                addressDetails={customerAddress}
                onPress={onPress}
              />
            </LI>
          );
        })}
      </UL>
    </View>
  );
});

export const LocationSearchPredictionAddressesList = memo(
  (props: LocationSearchPredictionAddressesListProps) => {
    const { addresses, onPress } = props;

    const { match } = useResponsive();
    const a11ySelector = useA11ySelector('locations.delivery.addresses-container'); // prettier-ignore

    return (
      <View style={match([styles.containerXS, styles.containerSM])} {...a11ySelector.props}>
        <UL>
          {addresses.map((address) => {
            return (
              <LI key={address.id}>
                <LocationSearchAddress
                  variation="prediction-address"
                  address={address}
                  onPress={onPress}
                />
              </LI>
            );
          })}
        </UL>
      </View>
    );
  },
);

// ─── Subcomponents ───────────────────────────────────────────────────────────

const LocationSearchAddress = (props: LocationSearchAddressProps) => {
  const { variation, onPress } = props;

  const { id, name, address, matchedSubstrings, description } = props.address;

  const fullAddress = name ? `${capitalizeFirstLetter(name)} · ${address}` : address;
  const highlights = matchedSubstrings ? getSubstringsByPositions(address, matchedSubstrings) : [];

  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onAddressBtnPress = useCallback(() => {
    if (variation === 'customer-address') {
      onPress(props.addressDetails);

      return;
    }

    onPress(id, description ?? address);
  }, [variation, onPress, id, address, description, props.addressDetails]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableRef = useRef<View>(null);
  // @ts-expect-error - React ref type mismatch
  const { isHovered, isFocused, isActive } = usePressableState(pressableRef);

  const pressableStyles = [
    styles.addressPressable,
    isHovered || isFocused ? styles.addressPressableHover : undefined,
    isActive ? styles.addressPressableActive : undefined,
    addressPressableWebStyles,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={pressableRef}
      accessibilityRole="button"
      accessibilityHint={formatMessage(messages.addressesAddressHint)}
      accessibilityLabel={formatMessage(messages.addressesAddressLabel, {
        address,
      })}
      style={pressableStyles}
      onPress={onAddressBtnPress}
    >
      <View>
        <LocationSearchAddressIcon name={name} />
      </View>

      <View style={styles.addressPressableContent} pointerEvents="none">
        <HighlightWords
          text={fullAddress}
          highlights={highlights}
          selectable={false}
          numberOfLines={2}
        />
      </View>

      <View>
        <Icon name="IconCaretRight" width={16} height={16} />
      </View>
    </Pressable>
  );
};

const LocationSearchAddressIcon = (props: Pick<LocationAddress, 'name'>) => {
  const { name } = props;

  if (name === 'home') return <Icon name="IconHomeStrokeSm" />;

  if (name === 'work') return <Icon name="IconWorkStrokeSm" />;

  return <Icon name="IconCustomStrokeSm" />;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addressesAddressHint: {
    defaultMessage: 'Selects address',
    description: 'Addresses | Address Hint',
  },
  addressesAddressLabel: {
    defaultMessage: 'Select {address} address',
    description: 'Addresses | Address Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    padding: theme.spacing['2'],
  },
  containerSM: {
    padding: theme.spacing['2'],
  },

  addressPressable: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['2'],
    paddingVertical: theme.spacing['4'],
    borderRadius: theme.radius.small,
  },
  addressPressableHover: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  addressPressableActive: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addressPressableContent: {
    flex: 1,
    marginHorizontal: theme.spacing['4'],
  },
});

const addressPressableWebStyles = webOnlyStyles({
  transition: `background-color ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchPredictionAddressesListProps = {
  addresses: LocationAddress[] | readonly LocationAddress[];
  onPress: (placeId: string, placeAddress: string) => void;
};

type LocationSearchAddressesListProps = {
  addresses: AddressDetails[] | readonly AddressDetails[];
  onPress: (addressDetails: AddressDetails) => void;
};

type LocationSearchAddressProps =
  | {
      variation: 'customer-address';
      address: LocationAddress;
      addressDetails: AddressDetails;
      onPress: (addressDetails: AddressDetails) => void;
    }
  | {
      variation: 'prediction-address';
      address: LocationAddress;
      addressDetails?: never;
      onPress: (placeId: string, placeAddress: string) => void;
    };

type LocationAddress = {
  id: string;
  name?: string;
  address: string;
  description?: string;
  // @ts-expect-error TS(2503): Cannot find namespace 'google'.
  matchedSubstrings?: readonly google.maps.places.PredictionSubstring[];
};
